import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Folder, FileText, ArrowLeft, FileSpreadsheet, File } from "lucide-react";

const ICONS = {
  pdf: FileText,
  docx: FileText,
  xlsx: FileSpreadsheet,
  default: File,
};

export default function HRPage() {
  const [libraries, setLibraries] = useState([]);
  const [active, setActive] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/hr/libraries").then((r) => { setLibraries(r.data); setLoading(false); });
  }, []);

  const openLibrary = async (lib) => {
    setActive(lib);
    setDocs([]);
    const { data } = await api.get(`/hr/libraries/${lib.id}/documents`);
    setDocs(data);
  };

  return (
    <AppLayout title="HR Hub" subtitle="SharePoint document libraries">
      {!active ? (
        loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-32 bg-white border border-slate-200 rounded-lg shimmer" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {libraries.map((l) => (
              <button
                key={l.id}
                data-testid={TID.hrLibraryCard(l.id)}
                onClick={() => openLibrary(l)}
                className="text-left bg-white border border-slate-200 rounded-lg p-5 card-hover"
              >
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <Folder className="w-5 h-5 text-gbe" />
                  </div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full border border-violet-200 bg-violet-50 text-violet-800 font-medium">SharePoint</span>
                </div>
                <div className="mt-4 font-display text-lg font-semibold text-slate-900">{l.name}</div>
                <div className="mt-1 text-xs text-slate-500">{l.count} documents · updated {l.modified}</div>
              </button>
            ))}
          </div>
        )
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3">
            <button
              data-testid={TID.hrBack}
              onClick={() => { setActive(null); setDocs([]); }}
              className="w-8 h-8 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </button>
            <div>
              <div className="text-xs text-slate-500">HR Hub · Library</div>
              <div className="font-display text-lg font-semibold text-slate-900">{active.name}</div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                {["Name", "Type", "Size", "Modified", "Author"].map((c) => (
                  <th key={c} className="text-left font-medium px-5 py-2.5">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const Icon = ICONS[d.type] || ICONS.default;
                return (
                  <tr key={d.id} data-testid={TID.hrDocRow(d.id)} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <Icon className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-800 font-medium">{d.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-600 uppercase text-xs">{d.type}</td>
                    <td className="px-5 py-3 text-slate-600 tabular-nums">{d.size}</td>
                    <td className="px-5 py-3 text-slate-600 tabular-nums">{d.modified}</td>
                    <td className="px-5 py-3 text-slate-600">{d.author}</td>
                  </tr>
                );
              })}
              {docs.length === 0 && (
                <tr><td colSpan={5} className="text-center text-sm text-slate-500 py-10">Loading documents…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </AppLayout>
  );
}
